/**
 * App - Main routing with lazy-loaded pages
 */
import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from '@mzanzi/auth'
import { msalConfig } from './auth'
import { ErrorBoundary } from './components/error-boundary'

// Eager: landing, login, callback (initial load paths)
import { LandingPage } from './pages/Landing'
import { LoginPage } from './pages/Login'
import { AuthCallbackPage } from './pages/AuthCallback'

// Lazy: everything behind auth
const DashboardPage = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.DashboardPage })))
const CatalogPage = lazy(() => import('./pages/Catalog').then(m => ({ default: m.CatalogPage })))
const ProductsPage = lazy(() => import('./pages/Products').then(m => ({ default: m.ProductsPage })))
const ProductDetailPage = lazy(() => import('./pages/ProductDetail').then(m => ({ default: m.ProductDetailPage })))
const OrdersPage = lazy(() => import('./pages/Orders').then(m => ({ default: m.OrdersPage })))
const OrderDetailPage = lazy(() => import('./pages/OrderDetail').then(m => ({ default: m.OrderDetailPage })))
const SubscriptionsPage = lazy(() => import('./pages/Subscriptions').then(m => ({ default: m.SubscriptionsPage })))
const SubscriptionDetailPage = lazy(() => import('./pages/SubscriptionDetail').then(m => ({ default: m.SubscriptionDetailPage })))
const TenantsPage = lazy(() => import('./pages/Tenants').then(m => ({ default: m.TenantsPage })))
const SecurityPage = lazy(() => import('./pages/Security').then(m => ({ default: m.SecurityPage })))
const AnalyticsPage = lazy(() => import('./pages/Analytics').then(m => ({ default: m.AnalyticsPage })))
const NotificationsPage = lazy(() => import('./pages/Notifications').then(m => ({ default: m.NotificationsPage })))
const SettingsPage = lazy(() => import('./pages/Settings').then(m => ({ default: m.SettingsPage })))
const CheckoutPage = lazy(() => import('./pages/Checkout').then(m => ({ default: m.CheckoutPage })))
const BillingPage = lazy(() => import('./pages/Billing').then(m => ({ default: m.BillingPage })))
const SupportPage = lazy(() => import('./pages/Support').then(m => ({ default: m.SupportPage })))
const ProfilePage = lazy(() => import('./pages/Profile').then(m => ({ default: m.ProfilePage })))
const PaymentCallbackPage = lazy(() => import('./pages/PaymentCallback').then(m => ({ default: m.PaymentCallbackPage })))
const MatchQueuePage = lazy(() => import('./pages/MatchQueue'))

// Layout
import { DashboardLayout } from './components/layout/DashboardLayout'
import { Toaster } from './components/ui/sonner'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
})

function PageLoader() {
  return (
    <div className="flex h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return children
}

function StaffRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!user?.isStaff) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

/** Wraps a lazy-loaded page with Suspense + ErrorBoundary so crashes are isolated per-route */
function PageBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/catalog" element={<PageBoundary><CatalogPage /></PageBoundary>} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Protected dashboard routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<PageBoundary><DashboardPage /></PageBoundary>} />
          <Route path="/tenants" element={<StaffRoute><PageBoundary><TenantsPage /></PageBoundary></StaffRoute>} />
          <Route path="/products" element={<PageBoundary><ProductsPage /></PageBoundary>} />
          <Route path="/products/:id" element={<PageBoundary><ProductDetailPage /></PageBoundary>} />
          <Route path="/orders" element={<PageBoundary><OrdersPage /></PageBoundary>} />
          <Route path="/orders/:id" element={<PageBoundary><OrderDetailPage /></PageBoundary>} />
          <Route path="/subscriptions" element={<PageBoundary><SubscriptionsPage /></PageBoundary>} />
          <Route path="/subscriptions/:id" element={<PageBoundary><SubscriptionDetailPage /></PageBoundary>} />
          <Route path="/matching" element={<StaffRoute><PageBoundary><MatchQueuePage /></PageBoundary></StaffRoute>} />
          <Route path="/security" element={<StaffRoute><PageBoundary><SecurityPage /></PageBoundary></StaffRoute>} />
          <Route path="/analytics" element={<StaffRoute><PageBoundary><AnalyticsPage /></PageBoundary></StaffRoute>} />
          <Route path="/notifications" element={<PageBoundary><NotificationsPage /></PageBoundary>} />
          <Route path="/settings" element={<PageBoundary><SettingsPage /></PageBoundary>} />
          <Route path="/checkout" element={<PageBoundary><CheckoutPage /></PageBoundary>} />
          <Route path="/payment/callback" element={<PageBoundary><PaymentCallbackPage /></PageBoundary>} />
          <Route path="/billing" element={<PageBoundary><BillingPage /></PageBoundary>} />
          <Route path="/support" element={<PageBoundary><SupportPage /></PageBoundary>} />
          <Route path="/profile" element={<PageBoundary><ProfilePage /></PageBoundary>} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider config={msalConfig}>
        <BrowserRouter>
          <ErrorBoundary>
            <AppRoutes />
            <Toaster />
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
